import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ListParams } from '@/lib/api/resource';

/**
 * §4.6's list state, held in the query string.
 *
 * In the URL rather than in component state so that a filtered list is a place
 * rather than a mood: an editor can bookmark "unpublished news in my faculty",
 * send it to a colleague, and get it back by pressing Back after opening a
 * record. Component state loses all three, and the third is the one people
 * notice — returning from an editor to page 1 of an unfiltered list.
 *
 * The defaults are absent from the URL rather than written into it, so a plain
 * `/news` is the canonical address of the unfiltered list.
 */
export const DEFAULT_PER_PAGE = 25;

export interface ListState {
  params: ListParams;
  search: string;
  sort: string | undefined;
  page: number;
  perPage: number;
  showTrashed: boolean;
  setSearch: (value: string) => void;
  setSort: (value: string) => void;
  setPage: (page: number) => void;
  setPerPage: (value: number) => void;
  setShowTrashed: (value: boolean) => void;
  setFilter: (key: string, value: string | null) => void;
  /** For `Pagination`, which is href-based on the public site too. */
  buildPageHref: (page: number) => string;
}

export function useListParams(defaultSort?: string): ListState {
  const [params, setParams] = useSearchParams();

  const search = params.get('search') ?? '';
  const sort = params.get('sort') ?? defaultSort;
  const page = Number(params.get('page') ?? '1');
  const perPage = Number(params.get('per_page') ?? String(DEFAULT_PER_PAGE));
  const showTrashed = params.get('trashed') === '1';

  const update = useCallback(
    (changes: Record<string, string | null>, resetPage = true) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }

          /*
           * Any change but paging returns to page 1. Filtering a 9-page list
           * down to 2 while sitting on page 5 otherwise shows an empty table,
           * which reads as "no results" rather than "no page 5".
           */
          if (resetPage) {
            next.delete('page');
          }

          return next;
        },
        // Replace, so paging through a list does not fill the history stack
        // with entries the Back button has to walk through.
        { replace: true },
      );
    },
    [setParams],
  );

  const queryParams = useMemo<ListParams>(() => {
    const collected: ListParams = {};

    for (const [key, value] of params.entries()) {
      collected[key] = value;
    }

    // `per_page` always travels, so the server's default and this one cannot
    // disagree about how many rows a page holds.
    collected.per_page = perPage;

    if (sort !== undefined) {
      collected.sort = sort;
    }

    return collected;
  }, [params, perPage, sort]);

  return {
    params: queryParams,
    search,
    sort,
    page,
    perPage,
    showTrashed,
    setSearch: (value) => update({ search: value }),
    setSort: (value) => update({ sort: value }),
    setPage: (next) => update({ page: next === 1 ? null : String(next) }, false),
    setPerPage: (value) => update({ per_page: value === DEFAULT_PER_PAGE ? null : String(value) }),
    setShowTrashed: (value) => update({ trashed: value ? '1' : null }),
    setFilter: (key, value) => update({ [key]: value }),
    buildPageHref: (target) => {
      const next = new URLSearchParams(params);

      if (target === 1) {
        next.delete('page');
      } else {
        next.set('page', String(target));
      }

      const query = next.toString();

      return query === '' ? '' : `?${query}`;
    },
  };
}
